import React, { useEffect, useRef, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useLanguage } from '../i18n';

// Lives inline in the Overview tab (not a header popover). Renders as a
// collapsed card by default; expands in normal document flow when tapped —
// this is deliberate: an in-flow block can never overflow off-screen the way
// a position:absolute popover anchored to a small header icon can on mobile.
export default function SupportChatWidget({ user, theme, t, translate }) {
  const { t: tDefault } = useLanguage();
  const tr = translate || tDefault; // allow either the hook directly or a passed-down translate fn

  const dark = theme === 'dark';
  const tokens = t || {
    cardBg:     dark ? 'rgba(255,255,255,0.04)' : '#FFFFFF',
    cardBorder: dark ? 'rgba(255,255,255,0.08)' : '#e5e7eb',
    text:       dark ? '#f1f5f9' : '#1B2D44',
    subtext:    dark ? '#94a3b8' : '#6b7280',
    inputBg:    dark ? 'rgba(255,255,255,0.06)' : '#FFFFFF',
  };

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role: 'user'|'assistant', text}
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const sendingRef = useRef(false); // synchronous guard — see RasaView.js for why this matters
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (open) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  if (!user) return null; // support chat is only for signed-in users

  const sendMessage = async (e) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || sendingRef.current) return;

    sendingRef.current = true;
    setSending(true);
    setErrorMsg(null);

    const userMsg = { role: 'user', text: trimmed };
    const historyForCall = messages; // snapshot before appending
    setMessages((prev) => [...prev, userMsg]);
    setInput('');

    try {
      const functions = getFunctions();
      const supportChat = httpsCallable(functions, 'supportChat');
      const res = await supportChat({ message: trimmed, history: historyForCall });
      setMessages((prev) => [...prev, { role: 'assistant', text: res.data.text }]);
    } catch (err) {
      setMessages((prev) => prev.slice(0, -1)); // remove the optimistic user message
      setInput(trimmed); // restore draft
      if (err.code === 'functions/resource-exhausted') {
        setErrorMsg(tr('supportChatLimitReached'));
      } else {
        setErrorMsg(err.message || tr('supportChatGenericError'));
      }
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  return (
    <div style={{
      backgroundColor: tokens.cardBg,
      border: `1px solid ${tokens.cardBorder}`,
      borderRadius: '1rem',
      overflow: 'hidden',
      marginBottom: '1.5rem',
    }}>
      {/* ── Collapsed header row — always visible, toggles the panel ── */}
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%', boxSizing: 'border-box',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1rem 1.25rem', background: 'none', border: 'none',
          cursor: 'pointer', textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{
            width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
            background: open
              ? 'linear-gradient(135deg, #4B2C5E, #008081)'
              : 'rgba(0,128,129,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.05rem',
          }}>
            💬
          </span>
          <div>
            <div style={{ fontWeight: '700', fontSize: '0.9rem', color: tokens.text }}>
              {tr('supportChatTitle')}
            </div>
            {!open && (
              <div style={{ fontSize: '0.75rem', color: tokens.subtext, marginTop: '0.1rem' }}>
                {tr('supportChatEmptyState')}
              </div>
            )}
          </div>
        </div>
        <span style={{
          color: tokens.subtext, fontSize: '0.8rem',
          transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s',
        }}>
          ▾
        </span>
      </button>

      {/* ── Expanded panel — normal document flow, full width of its card ── */}
      {open && (
        <div style={{ borderTop: `1px solid ${tokens.cardBorder}` }}>
          <div style={{ height: '360px', overflowY: 'auto', padding: '1rem' }}>
            {messages.length === 0 ? (
              <p style={{ color: tokens.subtext, fontSize: '0.85rem', textAlign: 'center', marginTop: '2rem' }}>
                {tr('supportChatEmptyState')}
              </p>
            ) : (
              messages.map((msg, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  marginBottom: '0.75rem'
                }}>
                  <div style={{
                    maxWidth: '80%', padding: '0.6rem 0.85rem', fontSize: '0.85rem', lineHeight: 1.5,
                    borderRadius: msg.role === 'user' ? '1rem 1rem 0.25rem 1rem' : '1rem 1rem 1rem 0.25rem',
                    background: msg.role === 'user' ? 'linear-gradient(135deg, #4B2C5E, #7c3aed)' : (dark ? 'rgba(255,255,255,0.06)' : '#f3f4f6'),
                    color: msg.role === 'user' ? '#ffffff' : tokens.text,
                    border: msg.role === 'user' ? 'none' : `1px solid ${tokens.cardBorder}`
                  }}>
                    {msg.text}
                  </div>
                </div>
              ))
            )}
            {sending && (
              <p style={{ color: tokens.subtext, fontSize: '0.8rem', fontStyle: 'italic' }}>{tr('supportChatTyping')}</p>
            )}
            <div ref={messagesEndRef} />
          </div>

          {errorMsg && (
            <p style={{ color: '#E05C5C', fontSize: '0.75rem', padding: '0 1rem 0.5rem', margin: 0, textAlign: 'center' }}>
              {errorMsg}
            </p>
          )}

          <form onSubmit={sendMessage} style={{ padding: '0.75rem', borderTop: `1px solid ${tokens.cardBorder}`, display: 'flex', gap: '0.5rem' }}>
            <input
              type="text" value={input} onChange={(e) => setInput(e.target.value)}
              placeholder={tr('supportChatPlaceholder')} disabled={sending}
              style={{
                flex: 1, minWidth: 0, padding: '0.6rem 0.75rem', borderRadius: '0.6rem',
                border: `1px solid ${tokens.cardBorder}`, background: tokens.inputBg,
                color: tokens.text, fontSize: '0.85rem', boxSizing: 'border-box'
              }}
            />
            <button
              type="submit" disabled={sending || !input.trim()}
              style={{
                padding: '0.6rem 1rem', borderRadius: '0.6rem', border: 'none', flexShrink: 0,
                background: 'linear-gradient(135deg, #4B2C5E, #7c3aed)', color: '#fff',
                fontWeight: '700', fontSize: '0.85rem', cursor: (sending || !input.trim()) ? 'not-allowed' : 'pointer',
                opacity: (sending || !input.trim()) ? 0.5 : 1
              }}
            >
              {tr('supportChatSend')}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}